import { motion, AnimatePresence } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Search, Menu, X, Wallet as WalletIcon, Coins, Ticket, Sparkles,
  UserCircle, ChevronDown, Twitter, Send, Github, Layers, Info, Globe, Check
} from 'lucide-react';
import { SUPPORTED_LANGUAGES } from '../i18n';
import { useSite } from '../context/SiteContext';
import { useAuth } from '../context/AuthContext';
import { useToken } from '../context/TokenContext';
import { useSolana } from '../context/WalletContext';
import { useLoginModal } from './LoginModal';
import { WinnersTicker } from './marketplace';
import { getLocalizedText } from './MultiLanguageInput';
import axios from 'axios';

const CT_API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NavSearch = () => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language || 'en';
  const [pools, setPools] = useState([]);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => { axios.get(`${CT_API}/pools`).then((r) => setPools(r.data || [])).catch(() => {}); }, []);

  const term = q.trim().toLowerCase();
  const results = term
    ? pools.filter((p) => (getLocalizedText(p.name, lang) || '').toLowerCase().includes(term)).slice(0, 6)
    : [];

  const submit = (e) => { e.preventDefault(); navigate(`/pools${q ? `?q=${encodeURIComponent(q)}` : ''}`); setOpen(false); };

  return (
    <div className="relative hidden xl:block">
      <form onSubmit={submit} className="flex items-center gap-2 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-10 w-56 focus-within:border-[#FF8C00] transition-colors">
        <Search size={16} className="text-[#9A8F79]" />
        <input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 180)}
          placeholder="Search packs..."
          data-testid="navbar-search-input"
          className="bg-transparent text-sm text-[#1A1206] placeholder:text-[#9A8F79] outline-none w-full"
        />
      </form>
      {open && term && (
        <div className="absolute right-0 mt-2 w-80 bg-white border border-[#F0E3C8] rounded-xl shadow-xl p-1.5 z-50 max-h-96 overflow-y-auto" data-testid="search-results">
          {results.length > 0 ? results.map((p) => (
            <button
              key={p.id}
              onMouseDown={(e) => { e.preventDefault(); navigate(`/pool/${p.id}`); setQ(''); setOpen(false); }}
              data-testid={`search-result-${p.id}`}
              className="w-full flex items-center gap-3 p-2 rounded-lg hover:bg-[#FFF8EA] text-left transition-colors"
            >
              {p.image_url && <img src={p.image_url} alt="" className="w-10 h-10 rounded-lg object-cover shrink-0" />}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-[#1A1206] truncate">{getLocalizedText(p.name, lang)}</p>
                <p className="text-xs text-[#FF6A00] font-bold flex items-center gap-1"><Coins size={12} /> {p.price}</p>
              </div>
            </button>
          )) : (
            <p className="text-sm text-[#9A8F79] px-3 py-3">No packs found.</p>
          )}
        </div>
      )}
    </div>
  );
};

const LOGO = '/cardtopia-logotext.webp';

const fmtCompact = (num) => {
  const n = Number(num) || 0;
  if (n >= 1e9) return (n / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
  if (n >= 1e6) return (n / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
  if (n >= 1e3) return (n / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
  return Math.floor(n).toString();
};

const NAV_LINKS = [
  { label: 'Packs', to: '/pools', icon: Layers },
  { label: 'About Us', to: '/about', icon: Info },
];

const pageVariants = {
  initial: { opacity: 0, y: 12 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
};

const isActive = (pathname, to) => {
  const base = to.split('#')[0];
  return pathname === base || (base === '/pools' && pathname.startsWith('/pool'));
};

const Logo = ({ siteSettings, onClick }) => (
  <button onClick={onClick} className="flex items-center gap-2.5 shrink-0" data-testid="navbar-logo">
    <img src={siteSettings?.logo || LOGO} alt={siteSettings?.app_name || 'Cardtopia'} className="h-7 object-contain" />
  </button>
);

const LanguageSwitcher = ({ compact = false }) => {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = SUPPORTED_LANGUAGES.find((l) => l.code === i18n.language)
    || SUPPORTED_LANGUAGES.find((l) => i18n.language?.startsWith(l.code))
    || SUPPORTED_LANGUAGES[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    try { localStorage.setItem('language', code); } catch (e) { /* ignore */ }
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="navbar-language"
        className="flex items-center gap-1.5 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-9 text-sm font-bold text-[#1A1206] hover:border-[#FF8C00] transition-colors"
      >
        <Globe size={15} className="text-[#FF8C00]" />
        <span className={`fi fi-${current.countryCode}`} />
        {!compact && <span className="uppercase">{current.code}</span>}
        <ChevronDown size={13} className="text-[#9A8F79]" />
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }}
            className="absolute right-0 mt-2 w-52 max-h-80 overflow-y-auto bg-white border border-[#F0E3C8] rounded-xl shadow-xl p-1.5 z-50"
            data-testid="language-menu"
          >
            {SUPPORTED_LANGUAGES.map((l) => (
              <button
                key={l.code}
                onMouseDown={(e) => { e.preventDefault(); change(l.code); }}
                data-testid={`language-option-${l.code}`}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm font-medium text-left transition-colors ${l.code === current.code ? 'bg-[#FFF3D6] text-[#FF6A00]' : 'text-[#1A1206] hover:bg-[#FFF8EA]'}`}
              >
                <span className={`fi fi-${l.countryCode}`} />
                <span className="flex-1">{l.nativeName}</span>
                {l.code === current.code && <Check size={15} className="text-[#FF8C00]" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { settings: siteSettings } = useSite();
  const { isAuthenticated, user } = useAuth();
  const { tokenSettings, getTokenIcon } = useToken();
  const { connected, tokenBalance, fetchTokenBalance } = useSolana();
  const { openLogin } = useLoginModal();

  const [drawer, setDrawer] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState('');

  const tokenActive = connected && tokenSettings?.is_active && tokenSettings?.contract_address;
  useEffect(() => {
    if (tokenActive) fetchTokenBalance(tokenSettings.contract_address, tokenSettings.decimals || 9);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connected, tokenSettings?.is_active, tokenSettings?.contract_address]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setDrawer(false); }, [location.pathname]);

  const showNav = !['/login', '/register'].includes(location.pathname) && !location.pathname.startsWith('/pool/');

  const go = (to) => {
    if (to.includes('#')) {
      const [path, hash] = to.split('#');
      if (location.pathname !== path) { navigate(path); setTimeout(() => document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' }), 350); }
      else document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    } else navigate(to);
  };

  const submitSearch = (e) => { e.preventDefault(); navigate(`/pools${q ? `?q=${encodeURIComponent(q)}` : ''}`); };

  if (!showNav) {
    return (
      <div className="min-h-screen bg-[#FFD400] text-[#1A1206]">
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} className="w-full">
            {children}
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="min-h-screen text-[#1A1206] bg-[#FFD400]">
      {/* Contract Address running text (above navbar) */}
      <a
        href="https://dexscreener.com/solana/CLrsRz21tZtHe7hkt34d8zC9t6gwLziw9SdMM6o1pump"
        target="_blank" rel="noopener noreferrer"
        className="fixed top-0 left-0 right-0 z-[60] h-8 bg-[#1A1206] overflow-hidden flex items-center border-b border-[#FFD400]/40 cursor-pointer"
        data-testid="ca-marquee"
      >
        <div className="ca-marquee-track">
          {[...Array(8)].map((_, i) => (
            <span key={i} className="inline-flex items-center gap-2 px-8 whitespace-nowrap text-xs font-semibold">
              <Sparkles size={12} className="text-[#FF8C00]" />
              <span className="text-white/60 uppercase tracking-widest">Contract Address</span>
              <span className="font-mono text-[#FFD400]">CLrsRz21tZtHe7hkt34d8zC9t6gwLziw9SdMM6o1pump</span>
              <span className="text-[#FF8C00] font-bold">· View on Dexscreener →</span>
            </span>
          ))}
        </div>
      </a>

      {/* Top Navbar */}
      <header
        className={`fixed top-8 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-white/85 backdrop-blur-xl border-b border-[#F0E3C8] shadow-[0_4px_24px_-12px_rgba(255,140,0,0.35)]' : 'bg-white/60 backdrop-blur-md border-b border-transparent'}`}
        data-testid="top-navbar"
      >
        <div className="max-w-[1500px] mx-auto px-4 lg:px-8 h-16 flex items-center gap-4">
          <Logo siteSettings={siteSettings} onClick={() => navigate('/home')} />

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 ml-4">
            {NAV_LINKS.map((l) => {
              const Ic = l.icon;
              return (
                <button
                  key={l.label}
                  onClick={() => go(l.to)}
                  data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}
                  className={`flex items-center gap-1.5 px-3.5 py-2 rounded-lg text-sm font-bold transition-colors ${isActive(location.pathname, l.to) ? 'text-[#FF6A00] bg-[#FFF3D6]' : 'text-[#564B38] hover:text-[#FF8C00] hover:bg-[#FFF8EA]'}`}
                >
                  <Ic size={15} /> {l.label}
                </button>
              );
            })}
          </nav>

          <div className="flex-1" />

          {/* Search (desktop) - live search */}
          <NavSearch />

          {/* Language switcher */}
          <div className="hidden sm:block"><LanguageSwitcher /></div>

          {/* Currency selector */}
          <button data-testid="navbar-currency" className="hidden sm:flex items-center gap-1.5 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-9 text-sm font-bold text-[#1A1206] hover:border-[#FF8C00] transition-colors">
            🇺🇸 USD <span className="text-[#FF8C00]">$</span> <ChevronDown size={13} className="text-[#9A8F79]" />
          </button>

          {/* Right actions (desktop) */}
          <div className="hidden lg:flex items-center gap-2">
            {isAuthenticated ? (
              <>
                <button onClick={() => navigate('/wallet')} data-testid="navbar-coins" className="flex items-center gap-1.5 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-9 hover:border-[#FFC300] transition-colors">
                  <Coins size={15} className="text-[#FF8C00]" />
                  <span className="text-sm font-bold">{user?.coins?.toFixed(0) || 0}</span>
                </button>
                {tokenActive && (
                  <button onClick={() => navigate('/wallet')} data-testid="navbar-token" className="flex items-center gap-1.5 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-9 hover:border-[#FF8C00] transition-colors">
                    {getTokenIcon() ? <img src={getTokenIcon()} alt="" className="w-4 h-4 rounded-full" /> : <Sparkles size={14} className="text-[#FF8C00]" />}
                    <span className="text-sm font-bold">{fmtCompact(tokenBalance)}</span>
                  </button>
                )}
                <button onClick={() => navigate('/wallet')} data-testid="navbar-tickets" className="flex items-center gap-1.5 bg-[#FFF8EA] border border-[#F0E3C8] rounded-full px-3 h-9 hover:border-[#FF6A00] transition-colors">
                  <Ticket size={15} className="text-[#FF6A00]" />
                  <span className="text-sm font-bold">{fmtCompact(user?.tickets || 0)}</span>
                </button>
                <button onClick={() => navigate('/profile')} data-testid="navbar-account" className="flex items-center gap-2 bg-gradient-to-r from-[#FFC300] to-[#FF8C00] rounded-full pl-2 pr-3 h-9 text-[#1A1206] hover:shadow-[0_0_18px_rgba(255,140,0,0.45)] transition-all">
                  <UserCircle size={20} />
                  <span className="text-sm font-bold max-w-[110px] truncate">{user?.username || 'Account'}</span>
                </button>
              </>
            ) : (
              <button onClick={() => openLogin()} data-testid="navbar-connect-wallet" className="flex items-center gap-2 bg-white border border-[#F0E3C8] rounded-full px-4 h-10 text-sm font-bold text-[#1A1206] hover:border-[#FF8C00] hover:text-[#FF6A00] transition-colors">
                <WalletIcon size={16} /> Connect Wallet
              </button>
            )}
          </div>

          {/* Mobile hamburger */}
          <button onClick={() => setDrawer(true)} className="lg:hidden w-10 h-10 flex items-center justify-center rounded-lg bg-[#FFF8EA] border border-[#F0E3C8]" data-testid="navbar-hamburger">
            <Menu size={22} className="text-[#1A1206]" />
          </button>
        </div>
      </header>

      {/* Mobile drawer */}
      <AnimatePresence>
        {drawer && (
          <>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setDrawer(false)} className="fixed inset-0 z-[60] bg-black/40 backdrop-blur-sm lg:hidden" />
            <motion.div initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', stiffness: 300, damping: 30 }} className="fixed top-0 right-0 bottom-0 z-[70] w-[82%] max-w-xs bg-white border-l border-[#F0E3C8] p-5 flex flex-col lg:hidden" data-testid="mobile-drawer">
              <div className="flex items-center justify-between mb-6">
                <Logo siteSettings={siteSettings} onClick={() => { setDrawer(false); navigate('/home'); }} />
                <button onClick={() => setDrawer(false)} className="w-9 h-9 flex items-center justify-center rounded-lg bg-[#FFF8EA]" data-testid="drawer-close"><X size={20} /></button>
              </div>
              <nav className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => {
                  const Ic = l.icon;
                  return (
                    <button key={l.label} onClick={() => go(l.to)} className={`flex items-center gap-2 text-left px-3 py-3 rounded-xl text-base font-semibold ${isActive(location.pathname, l.to) ? 'bg-[#FFF3D6] text-[#FF6A00]' : 'text-[#564B38] hover:bg-[#FFF8EA]'}`} data-testid={`drawer-nav-${l.label.toLowerCase().replace(/\s+/g, '-')}`}>
                      <Ic size={18} /> {l.label}
                    </button>
                  );
                })}
              </nav>
              <div className="mt-auto pt-5 border-t border-[#F0E3C8] space-y-2">
                <div className="flex items-center justify-between px-1 pb-1">
                  <span className="text-sm font-semibold text-[#6E6452]">Language</span>
                  <LanguageSwitcher />
                </div>
                {isAuthenticated ? (
                  <div className="grid grid-cols-3 gap-2 text-center">
                    <div className="bg-[#FFF8EA] rounded-xl py-2"><Coins size={16} className="text-[#FF8C00] mx-auto mb-1" /><span className="text-xs font-bold">{user?.coins?.toFixed(0) || 0}</span></div>
                    <div className="bg-[#FFF8EA] rounded-xl py-2"><Ticket size={16} className="text-[#FF6A00] mx-auto mb-1" /><span className="text-xs font-bold">{fmtCompact(user?.tickets || 0)}</span></div>
                    <button onClick={() => navigate('/profile')} className="bg-[#FFF8EA] rounded-xl py-2"><UserCircle size={16} className="text-[#FF8C00] mx-auto mb-1" /><span className="text-xs font-bold">Me</span></button>
                  </div>
                ) : (
                  <button onClick={() => { setDrawer(false); openLogin(); }} className="w-full flex items-center justify-center gap-2 h-11 rounded-xl bg-white border border-[#F0E3C8] font-bold" data-testid="drawer-connect-wallet"><WalletIcon size={18} /> Connect Wallet</button>
                )}
                <button onClick={() => { setDrawer(false); navigate('/pools'); }} className="w-full flex items-center justify-center gap-2 h-11 rounded-xl font-extrabold text-[#1A1206] bg-gradient-to-r from-[#FFD400] to-[#FF6A00]"><Sparkles size={16} /> Open Packs</button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Main content */}
      <main className="pt-24 min-h-screen">
        <AnimatePresence mode="wait">
          <motion.div key={location.pathname} variants={pageVariants} initial="initial" animate="animate" exit="exit" transition={{ duration: 0.2 }} className="w-full">
            {children}
          </motion.div>
        </AnimatePresence>
      </main>

      <Footer siteSettings={siteSettings} go={go} navigate={navigate} />
      <WinnersTicker />
    </div>
  );
};

const Footer = ({ siteSettings, go, navigate }) => (
  <footer className="border-t border-[#E6B800] mt-20 pt-12 pb-28 lg:pb-16 bg-[#FFC300]" data-testid="site-footer">
    <div className="max-w-[1500px] mx-auto px-4 lg:px-8">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
        <div className="col-span-2 lg:col-span-1">
          <Logo siteSettings={siteSettings} onClick={() => navigate('/home')} />
          <p className="text-[#6E6452] text-sm mt-3 leading-relaxed max-w-xs">
            Open Packs, Chase Legends. Premium TCG packs, rips, and rare collectibles — Cardtopia.
          </p>
          <div className="flex items-center gap-2 mt-4">
            {[
              { Ic: Twitter, href: 'https://x.com/cardtopiaco', label: 'X' },
              { Ic: Send, href: 'https://telegram.me/cardtopiaco', label: 'Telegram' },
              { Ic: Github, href: '#', label: 'Github' },
            ].map(({ Ic, href, label }, i) => (
              <a key={i} href={href} target={href !== '#' ? '_blank' : undefined} rel={href !== '#' ? 'noopener noreferrer' : undefined} aria-label={label} data-testid={`footer-social-${label.toLowerCase()}`} className="w-9 h-9 rounded-lg bg-white border border-[#F0E3C8] flex items-center justify-center text-[#564B38] hover:text-[#FF6A00] hover:border-[#FF8C00] transition-colors"><Ic size={16} /></a>
            ))}
          </div>
        </div>
        <div>
          <h4 className="text-[#1A1206] font-bold text-sm mb-3 font-['Unbounded']">Explore</h4>
          <ul className="space-y-2 text-sm text-[#6E6452]">
            <li><button onClick={() => navigate('/pools')} className="hover:text-[#FF6A00]">Packs</button></li>
            <li><button onClick={() => navigate('/pools')} className="hover:text-[#FF6A00]">Rips</button></li>
            <li><button onClick={() => navigate('/inventory')} className="hover:text-[#FF6A00]">My Cards</button></li>
            <li><button onClick={() => go('/home#top-chase')} className="hover:text-[#FF6A00]">Top Chase Cards</button></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[#1A1206] font-bold text-sm mb-3 font-['Unbounded']">Information</h4>
          <ul className="space-y-2 text-sm text-[#6E6452]">
            <li><button onClick={() => navigate('/support')} className="hover:text-[#FF6A00]">Support</button></li>
            <li><button onClick={() => navigate('/swap')} className="hover:text-[#FF6A00]">Cash Out</button></li>
          </ul>
        </div>
        <div>
          <h4 className="text-[#1A1206] font-bold text-sm mb-3 font-['Unbounded']">Play Responsibly</h4>
          <p className="text-[#9A8F79] text-xs leading-relaxed">
            Cardtopia involves randomized gacha mechanics. Outcomes are based on chance. Please play responsibly and only spend what you can afford. 18+.
          </p>
        </div>
      </div>
      <div className="mt-10 pt-6 border-t border-[#F0E3C8] flex flex-col sm:flex-row items-center justify-between gap-3">
        <p className="text-[#9A8F79] text-xs">© {new Date().getFullYear()} {siteSettings?.app_name || 'Cardtopia'}. All rights reserved.</p>
        <p className="text-[#9A8F79] text-xs font-mono">Open Packs, Chase Legends</p>
      </div>
    </div>
  </footer>
);

export const PageHeader = ({ title, showBack = false, rightElement = null }) => {
  const navigate = useNavigate();
  return (
    <header className="sticky top-24 z-30 bg-white/90 backdrop-blur-lg border-b border-[#F0E3C8] px-5 lg:px-8 py-4">
      <div className="max-w-[1500px] mx-auto flex items-center justify-between">
        <div className="flex items-center gap-3">
          {showBack && (
            <button onClick={() => navigate(-1)} className="text-[#6E6452] hover:text-[#1A1206] transition-colors" data-testid="back-button">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7" /></svg>
            </button>
          )}
          <h1 className="text-xl lg:text-2xl font-bold uppercase tracking-wider text-[#1A1206] font-['Unbounded']">{title}</h1>
        </div>
        {rightElement}
      </div>
    </header>
  );
};
